/**
 * clayPressPipeline — a malleable clay-like surface that takes
 * permanent-looking fingerprints from bass. The displacement field
 * uses mostly smooth low-frequency components (no high-frequency
 * detail) and the shading is matte with subtle wet sheen.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface ClayPressPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(443.897, 441.423));
  p += dot(p, p + 19.19);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1, 0));
  float c = hash21(i + vec2(0, 1));
  float d = hash21(i + vec2(1, 1));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm2(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.03 + vec2(1.7, -3.1);
    a *= 0.5;
  }
  return v;
}

float mapClay(vec3 p) {
  // Base — slightly squashed sphere.
  vec3 e = vec3(1.1, 1.1, 0.95);
  float d = length(p / e) - 1.0;
  // Slow drift of the entire surface — clay slowly reshapes.
  d += (fbm2(p.xy * 0.8 + vec2(uTime * 0.05, -uTime * 0.04)) - 0.5) * 0.18;
  // Bass presses — three large soft dimples.
  float t = uTime;
  vec3 c1 = vec3(0.45 * cos(t * 0.32), 0.45 * sin(t * 0.29), 0.55 * sin(t * 0.21));
  vec3 c2 = vec3(0.45 * sin(t * 0.41 + 2.0), 0.45 * cos(t * 0.35 - 1.0), 0.55 * cos(t * 0.27));
  vec3 c3 = vec3(-0.4 * cos(t * 0.37 - 1.3), -0.4 * sin(t * 0.43), 0.55 * sin(t * 0.31 + 1.5));
  d -= uBass.x * exp(-dot(p - c1, p - c1) * 3.0) * 0.42;
  d -= uBass.y * exp(-dot(p - c2, p - c2) * 3.0) * 0.42;
  d -= uBass.z * exp(-dot(p - c3, p - c3) * 3.0) * 0.42;
  // Mid adds rolling clay waves.
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  mid = pow(clamp(mid * 2.8, 0.0, 1.0), 0.8);
  d += sin(p.x * 2.5 + t * 0.7) * cos(p.y * 2.2 - t * 0.5) * 0.06 * (0.4 + mid);
  return d;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(
    mapClay(p + e.xyy) - mapClay(p - e.xyy),
    mapClay(p + e.yxy) - mapClay(p - e.yxy),
    mapClay(p + e.yyx) - mapClay(p - e.yyx)
  ));
}

void main() {
  vec2 uv = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  uv.x *= uResolution.x / uResolution.y;
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.2, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 2.8, 0.0, 1.0), 0.8);
  high = pow(clamp(high * 3.2, 0.0, 1.0), 0.85);

  vec3 ro = vec3(0.0, 0.0, 3.4);
  vec3 rd = normalize(vec3(uv, -1.6));

  float t = 0.0;
  bool hit = false;
  vec3 p = ro;
  for (int i = 0; i < 90; i++) {
    float d = mapClay(p);
    if (d < 0.001) { hit = true; break; }
    if (t > 8.0) break;
    t += d * 0.9;
    p = ro + rd * t;
  }
  vec3 col = vec3(0.07, 0.05, 0.04);
  if (hit) {
    vec3 n = calcNormal(p);
    vec3 ldir = normalize(vec3(0.5, 0.7, 0.5));
    float diff = clamp(dot(n, ldir), 0.0, 1.0);
    float wrap = clamp(dot(n, ldir) * 0.5 + 0.5, 0.0, 1.0);
    // Wet sheen — narrow specular highlight.
    vec3 h = normalize(ldir - rd);
    float spec = pow(clamp(dot(n, h), 0.0, 1.0), 64.0);
    // Matte base — soft warm clay color with subtle tonal variation.
    vec3 base = mix(vec3(0.65, 0.42, 0.28), vec3(0.78, 0.55, 0.38), wrap);
    // Wet zone — darker where the bass has just pressed.
    float wet = clamp(-(length(p) - 1.0) * 4.0, 0.0, 1.0);
    base = mix(base, base * 0.5 + vec3(0.05, 0.04, 0.06), wet * bass);
    col = base * (0.4 + diff * 0.7);
    col += spec * vec3(1.0) * (0.4 + bass * 0.7);
    // Subtle fingerprint grain — readable texture in dimples.
    float grain = fbm2(p.xy * 12.0 + p.z * 4.0);
    col *= 0.85 + 0.15 * grain;
  }

  float vig = pow(16.0 * vUV.x * (1.0 - vUV.x) * vUV.y * (1.0 - vUV.y), 0.25);
  col *= vig;
  col = col / (1.0 + col);
  col = pow(col, vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}`;

export function computeClayPressBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.85);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.6));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.4);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.05 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.65 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.4 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 3.9 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 5.7 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 7.8 + 2.1);
}

export function computeClayPressRealtimeBands(freqData: Uint8Array, out: Float32Array): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.008], [0.008, 0.030], [0.030, 0.060],
    [0.060, 0.150], [0.150, 0.260], [0.260, 0.420],
    [0.420, 0.620], [0.620, 0.800], [0.800, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    let s = 0, c = 0;
    const a = Math.floor(lo * N);
    const b = Math.floor(hi * N);
    for (let k = a; k < b; k++) { s += freqData[k]; c++; }
    out[i] = c > 0 ? (s / c) / 255 : 0;
  }
}

export function createClayPressPipeline(gl: WebGL2RenderingContext): ClayPressPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawClayPress(
  gl: WebGL2RenderingContext,
  pipe: ClayPressPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}